import { Box, Grid } from '@joinrepublic/dls-ui-kit';

import { useAnalytics } from '@r-client/shared/data/analytics';
import { DangerousHTML } from '@r-client/shared/util/components';

import { IBannerQuery } from '../../graphql/types';

const FLASHBAR_HEIGHT = '65px';

export type ActiveFlashbarBannerData = Extract<
  IBannerQuery['banner'],
  { __typename: 'ActiveFlashbarBanner' }
>;

export interface IActiveFlashbarBannerProps {
  banner: ActiveFlashbarBannerData;
}

export function ActiveFlashbarBanner({
  banner: {
    key,
    backgroundColor,
    backgroundMobileUrl,
    backgroundUrl,
    bannerCenterContent,
    bannerCta,
    bannerLeftContent,
    bannerLink,
    textColor,
  },
}: IActiveFlashbarBannerProps) {
  const color = textColor ?? 'white';
  const bgColor = backgroundColor ?? 'black';
  const hasCenterContent = !!bannerCenterContent;

  const analytics = useAnalytics();
  const handleClick = () => {
    analytics.track({
      name: 'promo_flash_banner_clicked',
      params: { label: key },
    });
  };

  return (
    <Grid
      as={bannerLink ? 'a' : 'div'}
      {...(bannerLink
        ? {
            href: bannerLink,
            _hover: { textDecoration: 'none', color },
            _focus: { color },
            _active: { color },
          }
        : {})}
      role="group"
      columnGap="6"
      rowGap="1.5"
      templateColumns={{
        base: 'auto',
        sm: hasCenterContent ? '1fr auto 1fr' : '1fr auto',
      }}
      alignItems="center"
      minH={FLASHBAR_HEIGHT}
      px="4"
      py={{ base: '3', sm: '0' }}
      bg={bgColor}
      bgImage={{
        base: backgroundMobileUrl ? `url('${backgroundMobileUrl}')` : 'none',
        sm: backgroundUrl ? `url('${backgroundUrl}')` : 'none',
      }}
      bgRepeat="no-repeat"
      bgPosition="50% 50%"
      bgSize={{ base: 'cover', sm: 'auto 100%' }}
      fontSize="16px"
      fontWeight="400"
      textAlign="center"
      color={color}
      sx={{
        // Ensures that r-client visually matches the monolith flashbar.
        // Remove when the same font with the same configuration is used everywhere.
        fontVariationSettings: "'opsz' 24",
        // Rich HTML content from the backend may include html tags that require specific styling to match the monolith flashbar.
        img: {
          maxH: FLASHBAR_HEIGHT,
          verticalAlign: 'middle',
          display: 'inline-block',
          objectFit: 'contain',
        },
        u: {
          textUnderlineOffset: '5px',
          textDecorationThickness: '1px',
          textDecorationColor: `color-mix(in srgb, ${color} 40%, transparent)`,
        },
        strong: {
          fontWeight: '600',
        },
        b: {
          fontWeight: '600',
        },
      }}
      onClick={handleClick}
    >
      {bannerLeftContent ? (
        <Box textAlign={{ base: 'center', sm: 'left' }}>
          <DangerousHTML html={bannerLeftContent} component="div" />
        </Box>
      ) : (
        <Box />
      )}

      {hasCenterContent && (
        <Box justifySelf="center">
          <DangerousHTML html={bannerCenterContent} component="div" />
        </Box>
      )}

      {bannerCta && (
        <Box
          justifySelf={{ base: 'stretch', sm: 'end' }}
          mt={{ base: '1.5', sm: '0' }}
          w={{ base: '100%', sm: 'auto' }}
          minW="40"
          px={{ base: '4', sm: '5' }}
          py={{ base: '2', sm: '9px' }}
          border="1px solid"
          borderColor={`color-mix(in srgb, ${color} 40%, transparent)`}
          borderRadius={{ base: 'base', sm: 'md' }}
          color={color}
          fontSize={{ base: '14px', sm: '16px' }}
          whiteSpace="nowrap"
          transition="border-color 0.2s ease-in-out"
          {...(bannerLink
            ? {
                _groupHover: {
                  borderColor: `color-mix(in srgb, ${color} 75%, transparent)`,
                },
              }
            : {})}
        >
          {bannerCta}
        </Box>
      )}
    </Grid>
  );
}
